import { useCallback, useEffect, useState } from 'react';
import { Gift, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '../services/supabase';
import { etiquetaVariante } from '../lib/variantes';

// Regalar cualquier cosa del catálogo desde la ficha del alumno (mig 246/247).
//
// POR QUÉ ES UNA PANTALLA APARTE DE "REGALAR SESIONES"
// ---------------------------------------------------
// Parecen lo mismo y no lo son. "Regalar sesiones" (`admin_grant_credits`)
// concede N sesiones sueltas de un tipo, con la caducidad que le pongas: no
// hay ningún artículo detrás. Esto concede un ARTÍCULO DEL CATÁLOGO, con su
// caducidad, su reparto y —si es ropa— su talla y su descuento de inventario.
// Unificarlas obligaría a inventar un producto para cada regalo a medida, así
// que conviven.
//
// LOS PRECIOS NO SE MANDAN. La RPC los lee del catálogo; aquí solo se enseñan
// para que quien regala sepa lo que está regalando.

interface PackRow {
  id: string;
  name: string;
  price_cents: number;
  credit_type: string | null;
  sessions_count: number;
}

interface VarianteRow {
  id: string;
  attributes: Record<string, string>;
  stock: number;
  position: number;
  active: boolean;
}

interface ProductoRow {
  id: string;
  name: string;
  price_cents: number;
  fulfillment_type: string;
  product_variants: VarianteRow[] | null;
}

/** Lo elegible: un bono o un producto, ya normalizado para el desplegable. */
interface Articulo {
  clave: string;
  tipo: 'session_pack' | 'product';
  id: string;
  nombre: string;
  precio_cents: number;
  grupo: string;
  /** Solo la ropa: las tallas entre las que hay que elegir. */
  variantes: VarianteRow[];
  /** Concede Preparación Física: para un menor, arrastra la parental. */
  concede_pf: boolean;
}

interface PushDevuelto {
  user_id?: string;
  title?: string;
  body?: string;
  data?: unknown;
}

interface ResultadoRegalo {
  ok?: boolean;
  error?: string;
  order_id?: string;
  has_physical?: boolean;
  value_cents?: number;
  pushes?: PushDevuelto[];
}

const MOTIVOS = [
  'Sesión de prueba',
  'Primera clase cortesía',
  'Compensación',
  'Promoción',
  'Captación',
];

export const formatearPrecio = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

/** Qué se puede regalar hoy, y qué no. */
export function esRegalable(): boolean {
  // [254] Ya no excluye nada. Preparación Física estaba fuera porque arrastra
  // la autorización parental del menor y no había dónde sellarla en un regalo;
  // ahora la sella `admin_gift_order`, con el hijo como sujeto y el tutor como
  // actor. La función se conserva —en vez de borrar las dos llamadas— porque
  // el día que algo vuelva a no ser regalable, este es el sitio.
  return true;
}

/** ¿Este artículo concede acceso a Preparación Física? */
export function concedePF(articulo: {
  tipo: string;
  fulfillment_type?: string;
  credit_type?: string | null;
}): boolean {
  if (articulo.tipo === 'product') return articulo.fulfillment_type === 'gym_tecnif_subscription';
  return (articulo.credit_type ?? 'grupal') === 'gym';
}

interface Props {
  studentId: string;
  studentName: string;
  /**
   * [254] Para PF hace falta la autorización del tutor, y solo si el alumno es
   * menor. Llegan de la ficha, que ya tiene el perfil cargado, en vez de
   * volver a consultarlo aquí.
   *
   * `esMenor` sigue el mismo criterio que la RPC: edad desconocida = menor.
   */
  esMenor: boolean;
  nombreTutor: string | null;
  onClose(): void;
  onHecho(): void;
}

export default function RegalarProductoModal({
  studentId,
  studentName,
  esMenor,
  nombreTutor,
  onClose,
  onHecho,
}: Props) {
  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');

  const [clave, setClave] = useState('');
  // Se guarda como TEXTO, no como número. Con un número había que forzar el
  // mínimo en cada tecla, y eso impide vaciar el campo: borrar el 1 lo repone
  // al instante y teclear un 2 encima deja 12. El mínimo se exige al enviar.
  const [cantidad, setCantidad] = useState('1');
  const [varianteId, setVarianteId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    const [packs, productos] = await Promise.all([
      supabase
        .from('session_packs')
        .select('id, name, price_cents, credit_type, sessions_count')
        .eq('active', true)
        .order('price_cents'),
      supabase
        .from('products')
        .select(
          'id, name, price_cents, fulfillment_type, product_variants(id, attributes, stock, position, active)'
        )
        .eq('active', true)
        .order('price_cents'),
    ]);

    // Sin catálogo no hay pantalla que enseñar: se dice, en vez de pintar un
    // desplegable vacío que parece que el club no vende nada.
    if (packs.error || productos.error) {
      setErrorCarga('No se pudo cargar el catálogo. Vuelve a abrirlo.');
      setCargando(false);
      return;
    }

    const lista: Articulo[] = [];
    for (const p of (packs.data ?? []) as PackRow[]) {
      if (!esRegalable()) continue;
      lista.push({
        clave: `session_pack:${p.id}`,
        tipo: 'session_pack',
        id: p.id,
        nombre: `${p.name} · ${p.sessions_count} ses.`,
        precio_cents: p.price_cents,
        grupo: 'Bonos',
        variantes: [],
        concede_pf: concedePF({ tipo: 'session_pack', credit_type: p.credit_type }),
      });
    }
    for (const p of (productos.data ?? []) as unknown as ProductoRow[]) {
      if (!esRegalable()) continue;
      lista.push({
        clave: `product:${p.id}`,
        tipo: 'product',
        id: p.id,
        nombre: p.name,
        precio_cents: p.price_cents,
        grupo: p.fulfillment_type === 'physical_stock' ? 'Ropa y material' : 'Productos',
        // El orden lo da `position`, igual que en inventario.
        variantes: [...(p.product_variants ?? [])]
          .filter((v) => v.active)
          .sort((a, b) => a.position - b.position),
        concede_pf: concedePF({ tipo: 'product', fulfillment_type: p.fulfillment_type }),
      });
    }
    setArticulos(lista);
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const elegido = articulos.find((a) => a.clave === clave) ?? null;
  const pideTalla = (elegido?.variantes.length ?? 0) > 0;
  const cantidadNum = /^\d+$/.test(cantidad) ? parseInt(cantidad, 10) : 0;

  // Cambiar de artículo tiene que soltar la talla del anterior: si no, se
  // mandaría la variante de otra prenda y la RPC lo rechazaría con un error
  // que no dice nada de lo que acaba de pasar.
  function elegirArticulo(nuevaClave: string) {
    setClave(nuevaClave);
    setVarianteId('');
    setError('');
  }

  const grupos = ['Bonos', 'Productos', 'Ropa y material'].filter((g) =>
    articulos.some((a) => a.grupo === g)
  );

  async function regalar() {
    if (!elegido) return;
    setEnviando(true);
    setError('');

    const item: Record<string, unknown> = {
      type: elegido.tipo,
      id: elegido.id,
      quantity: cantidadNum,
    };
    if (pideTalla) item.variant_id = varianteId;

    const { data, error: errRpc } = await supabase.rpc('admin_gift_order', {
      p_student_id: studentId,
      p_items: [item],
      p_reason: motivo.trim() || undefined,
    });

    const res = (data ?? null) as ResultadoRegalo | null;
    if (errRpc || !res?.ok) {
      setEnviando(false);
      // El texto de la RPC es para leerlo: dice qué falta, no un código.
      setError(res?.error ?? errRpc?.message ?? 'No se pudo completar el regalo.');
      return;
    }

    // Los pushes los DEVUELVE la RPC ya resueltos —alumno y tutores— porque
    // quién es tutor de quién se decide en la base, no aquí. Van después de
    // que el regalo esté concedido y son best-effort: si Expo falla, el
    // regalo ya está hecho y el aviso in-app también.
    await Promise.all(
      (res.pushes ?? [])
        .filter((p) => p.user_id)
        .map((p) =>
          supabase.functions
            .invoke('gym-send-push', {
              body: {
                user_id: p.user_id,
                title: p.title,
                body: p.body,
                type: 'gift',
                data: p.data,
              },
            })
            .catch(() => {})
        )
    );

    setEnviando(false);
    onHecho();
    onClose();
  }

  // [254] PF a un menor sin datos del tutor: la RPC lo rechaza, y el botón no
  // se ofrece. Un botón que se pulsa para leer un error es peor que uno que
  // dice por qué no se puede antes de pulsarlo — el aviso de arriba lo explica.
  const faltaAutorizacionPF = !!elegido?.concede_pf && esMenor && !nombreTutor;
  const puedeEnviar =
    !!elegido &&
    cantidadNum >= 1 &&
    (!pideTalla || !!varianteId) &&
    !faltaAutorizacionPF &&
    !enviando;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => !enviando && onClose()}
      data-testid="modal-regalar-producto"
    >
      <div
        className="bg-bg-surface border border-bg-border rounded-xl p-6 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Gift size={20} className="text-gold-primary" />
          <h3 className="font-oswald text-lg text-white uppercase tracking-wider">
            Regalar producto
          </h3>
        </div>

        <p className="text-text-secondary text-sm">
          Regala cualquier artículo del catálogo a{' '}
          <strong className="text-white">{studentName}</strong>. No se cobra nada: queda un pedido a
          0 € y se le avisa a él y a sus tutores.
        </p>

        {cargando && (
          <p className="text-text-secondary text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando el catálogo…
          </p>
        )}

        {errorCarga && <p className="text-error text-sm">{errorCarga}</p>}

        {!cargando && !errorCarga && (
          <>
            <div>
              <label className="label" htmlFor="regalo-articulo">
                Qué se regala *
              </label>
              <select
                id="regalo-articulo"
                className="input"
                value={clave}
                onChange={(e) => elegirArticulo(e.target.value)}
                data-testid="select-regalo-articulo"
              >
                <option value="">— Elegir del catálogo —</option>
                {grupos.map((g) => (
                  <optgroup key={g} label={g}>
                    {articulos
                      .filter((a) => a.grupo === g)
                      .map((a) => (
                        <option key={a.clave} value={a.clave}>
                          {a.nombre} · {formatearPrecio(a.precio_cents)}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="regalo-cantidad">
                  Cantidad *
                </label>
                <input
                  id="regalo-cantidad"
                  type="number"
                  min={1}
                  className="input"
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  data-testid="input-regalo-cantidad"
                />
              </div>
              {pideTalla && (
                <div>
                  <label className="label" htmlFor="regalo-talla">
                    Talla *
                  </label>
                  <select
                    id="regalo-talla"
                    className="input"
                    value={varianteId}
                    onChange={(e) => setVarianteId(e.target.value)}
                    data-testid="select-regalo-talla"
                  >
                    <option value="">— Elegir —</option>
                    {elegido!.variantes.map((v) => (
                      <option key={v.id} value={v.id}>
                        {etiquetaVariante(v.attributes)} ({v.stock})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="label" htmlFor="regalo-motivo">
                Motivo
              </label>
              <select
                id="regalo-motivo"
                className="input mb-2"
                value={MOTIVOS.includes(motivo) ? motivo : ''}
                onChange={(e) => setMotivo(e.target.value)}
              >
                <option value="">— Elegir motivo —</option>
                {MOTIVOS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <input
                className="input"
                value={MOTIVOS.includes(motivo) ? '' : motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="O escribe un motivo personalizado…"
                aria-label="Motivo personalizado"
              />
            </div>

            {/* En voz alta, antes de confirmar. Regalar SALTA los requisitos que
                sí pide una compra: es lo acordado —el regalo sirve justo para
                quien todavía no es del club— pero quien lo hace tiene que
                saberlo, no descubrirlo después. */}
            <p className="text-text-secondary text-xs flex gap-2">
              <AlertTriangle size={14} className="text-gold-primary shrink-0 mt-0.5" />
              <span>
                El regalo no exige matrícula ni ningún otro requisito de compra. Si es ropa,
                descuenta stock y queda un pedido pendiente de preparar.
              </span>
            </p>

            {/* [254] PF a un menor sella una autorización parental en un
                registro que NO se puede corregir después: es append-only. Así
                que se dice a nombre de quién va a quedar ANTES de confirmar, no
                después. Si falta el dato del tutor, se dice aquí en vez de
                dejar que la RPC lo rechace con el botón ya pulsado. */}
            {elegido?.concede_pf && esMenor && (
              <p
                className="text-xs flex gap-2 bg-gold-primary/10 rounded p-2"
                data-testid="aviso-parental-pf"
              >
                <AlertTriangle size={14} className="text-gold-primary shrink-0 mt-0.5" />
                {nombreTutor ? (
                  <span className="text-text-secondary">
                    Preparación Física necesita la autorización del tutor. Va a quedar registrada a
                    nombre de <strong className="text-white">{nombreTutor}</strong>, como tutor de{' '}
                    {studentName}, y constará que la registraste tú desde el panel. No se puede
                    deshacer.
                  </span>
                ) : (
                  <span className="text-error">
                    La ficha de {studentName} no tiene los datos del tutor, y Preparación Física
                    necesita su autorización. Complétalos en la ficha antes de regalarla.
                  </span>
                )}
              </p>
            )}

            {error && (
              <p className="text-error text-sm" data-testid="error-regalo">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={enviando} className="btn-ghost">
                Cancelar
              </button>
              <button
                onClick={regalar}
                disabled={!puedeEnviar}
                className="btn-gold flex items-center gap-2"
                data-testid="btn-regalo-submit"
              >
                <Gift size={14} /> {enviando ? 'Regalando…' : 'Regalar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
